import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Descriptions,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsApi, ServerConfig, SyncAllServersResponse } from '../services/settingsApi';

const { Title, Text } = Typography;

const ServerManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingServer, setEditingServer] = useState<ServerConfig | null>(null);
  const [visibleApiKeys, setVisibleApiKeys] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // Fetch all servers
  const { data: servers = [], isLoading, refetch, error } = useQuery(
    'servers',
    () => settingsApi.getAllServers(),
    {
      refetchOnWindowFocus: false,
      onError: (err: any) => {
        console.error('Error fetching servers:', err);
        message.error('Failed to fetch servers');
      },
      onSuccess: (data) => {
        console.log('Servers fetched:', data);
      },
    }
  );

  // Add server mutation
  const addServerMutation = useMutation(
    (config: { domain: string; dirweb_x_api_key: string; label?: string }) =>
      settingsApi.addServer(config),
    {
      onSuccess: (data) => {
        console.log('Server added:', data);
        message.success('Server added successfully');
        setModalVisible(false);
        form.resetFields();
        queryClient.invalidateQueries('servers');
        // Also refetch to ensure we have the latest data
        refetch();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to add server');
      },
    }
  );

  // Update server mutation
  const updateServerMutation = useMutation(
    ({ id, updates }: { id: string; updates: Partial<{ domain: string; dirweb_x_api_key: string; label?: string }> }) =>
      settingsApi.updateServer(id, updates),
    {
      onSuccess: () => {
        message.success('Server updated successfully');
        setModalVisible(false);
        setEditingServer(null);
        form.resetFields();
        queryClient.invalidateQueries('servers');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to update server');
      },
    }
  );

  // Delete server mutation
  const deleteServerMutation = useMutation(
    (id: string) => settingsApi.deleteServer(id),
    {
      onSuccess: () => {
        message.success('Server deleted successfully');
        queryClient.invalidateQueries('servers');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to delete server');
      },
    }
  );

  // Sync servers mutation
  const syncServersMutation = useMutation(
    () => settingsApi.syncAllServers(),
    {
      onSuccess: (data: SyncAllServersResponse) => {
        if (data.success) {
          const successCount = data.successful || 0;
          const failedCount = data.failed || 0;
          
          if (failedCount === 0) {
            message.success(`Successfully synced ${successCount} server(s) to all configured servers`);
          } else {
            message.warning(`Sync completed: ${successCount} successful, ${failedCount} failed`);
          }
          
          // Show detailed results in modal if there are results
          if (data.results && data.results.length > 0) {
            Modal.info({
              title: 'Sync Results',
              width: 600,
              content: (
                <div>
                  <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Total Servers">{data.total}</Descriptions.Item>
                    <Descriptions.Item label="Successful">
                      <Tag color="success">{successCount}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Failed">
                      <Tag color="error">{failedCount}</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                  <Divider />
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {data.results.map((result, index) => (
                      <div key={index} style={{ marginBottom: 12, padding: 8, background: result.success ? '#f6ffed' : '#fff2f0', borderRadius: 4 }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div>
                            <Text strong>{result.label || result.domain}</Text>
                            {result.success ? (
                              <Tag color="success" style={{ marginLeft: 8 }}>Success</Tag>
                            ) : (
                              <Tag color="error" style={{ marginLeft: 8 }}>Failed</Tag>
                            )}
                          </div>
                          <Text type="secondary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                            {result.domain}
                          </Text>
                          {result.error && (
                            <Text type="danger" style={{ fontSize: '12px' }}>
                              Error: {result.error}
                            </Text>
                          )}
                          {result.responseTime && (
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              Response time: {result.responseTime}ms
                            </Text>
                          )}
                        </Space>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            });
          }
          
          queryClient.invalidateQueries('servers');
          refetch();
        } else {
          message.error('Sync failed');
        }
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to sync servers');
      },
    }
  );


  const handleAdd = () => {
    setEditingServer(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (server: ServerConfig) => {
    setEditingServer(server);
    form.setFieldsValue({
      domain: server.domain,
      dirweb_x_api_key: server.dirweb_x_api_key,
      label: server.label,
    });
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteServerMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingServer) {
        updateServerMutation.mutate({ id: editingServer.id, updates: values });
      } else {
        addServerMutation.mutate(values);
      }
    } catch (error) {
      // Form validation failed
    }
  };

  const toggleApiKeyVisibility = (id: string) => {
    const newVisible = new Set(visibleApiKeys);
    if (newVisible.has(id)) {
      newVisible.delete(id);
    } else {
      newVisible.add(id);
    }
    setVisibleApiKeys(newVisible);
  };

  const maskApiKey = (apiKey: string): string => {
    if (apiKey.length <= 8) return '****';
    return `${apiKey.substring(0, 4)}****${apiKey.substring(apiKey.length - 4)}`;
  };

  const columns = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (text: string, record: ServerConfig) => (
        <span style={{ fontWeight: text ? 500 : 400, color: text ? undefined : '#999' }}>
          {text || record.domain}
        </span>
      ),
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666' }}>{text}</span>
      ),
    },
    {
      title: 'API Key',
      dataIndex: 'dirweb_x_api_key',
      key: 'dirweb_x_api_key',
      render: (apiKey: string, record: ServerConfig) => {
        const isVisible = visibleApiKeys.has(record.id);
        return (
          <Space>
            <span style={{ fontFamily: 'monospace' }}>
              {isVisible ? apiKey : maskApiKey(apiKey)}
            </span>
            <Button
              type="link"
              size="small"
              icon={isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => toggleApiKeyVisibility(record.id)}
            />
          </Space>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) =>
        date ? new Date(date).toLocaleString() : 'N/A',
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) =>
        date ? new Date(date).toLocaleString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ServerConfig) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this server?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deleteServerMutation.isLoading}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Server Management
            </Title>
            <Tag color="blue">{servers.length} servers</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            >
              Refresh
            </Button>
            <Button
              icon={<CloudSyncOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Sync Config between Servers',
                  content: `This will sync all server configurations from this server to all ${servers.length} configured servers. Continue?`,
                  okText: 'Yes, Sync',
                  cancelText: 'Cancel',
                  onOk: () => syncServersMutation.mutate(),
                });
              }}
              loading={syncServersMutation.isLoading}
            >
              Sync Config between Servers
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add Server
            </Button>
          </Space>
        }
      >
        {error && (
          <div style={{ marginBottom: 16, color: 'red', padding: '12px', background: '#fff2f0', borderRadius: '4px' }}>
            Error loading servers: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}
        <Table
          columns={columns}
          dataSource={servers}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} servers`,
          }}
          locale={{
            emptyText: 'No servers configured. Click "Add Server" to get started.',
          }}
        />
      </Card>

      <Modal
        title={editingServer ? 'Edit Server' : 'Add Server'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setEditingServer(null);
          form.resetFields();
        }}
        okText={editingServer ? 'Update' : 'Add'}
        cancelText="Cancel"
        confirmLoading={addServerMutation.isLoading || updateServerMutation.isLoading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="label"
            label="Label (Optional)"
            tooltip="Human-readable name for this server (e.g., 'Server 1 - Production')"
          >
            <Input
              placeholder="Server 1 - Production"
              maxLength={100}
            />
          </Form.Item>

          <Form.Item
            name="domain"
            label="Domain (Full URL)"
            rules={[
              { required: true, message: 'Please enter domain URL' },
              {
                pattern: /^https?:\/\/.+/,
                message: 'Please enter a valid URL (e.g., http://198.998.871.22)',
              },
            ]}
          >
            <Input
              placeholder="http://198.998.871.22"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item
            name="dirweb_x_api_key"
            label="API Key (dirweb_x_api_key)"
            rules={[
              { required: true, message: 'Please enter API key' },
              { min: 4, message: 'API key must be at least 4 characters' },
            ]}
          >
            <Input.Password
              placeholder="Enter API key"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServerManagement;

